import { cloneDeep } from 'lodash';
import * as THREE from 'three';
import { Mesh, Vector2 } from 'three';
import BaseMesh from './base-mesh';
class HotArea extends BaseMesh {
    isMouseEnter = false;
    boldLineNum = -1;
    constructor(data) {
        super(data);
        this.update(data);
    }
    update(data, needsUpdate = true) {
        if (!this.isMouseEnter) {
            const { opacity = 1 } = data;
            const { points, normal, text, boldPoints } = data.renderProps;
            const { polygon, border, roiText } = normal;
            const { vertices, width, height, pointsCanvas } = this.handlePoints(points);
            const _vertices = [];
            vertices.map((item) => {
                _vertices.push(new Vector2(item.x, item.y));
            });
            this.boldLineNum = this.getBoldLineNum(points, boldPoints);
            this.geometry = new THREE.ShapeGeometry(new THREE.Shape(_vertices));
            this.assignUVs(this.geometry);
            this.material = new THREE.MeshBasicMaterial({
                map: this._draw(width, height, polygon, border, pointsCanvas, roiText, text),
                transparent: true,
                depthTest: false,
                opacity,
            });
        }
        if (needsUpdate) {
            super.update(data);
        }
        Object.keys(data).forEach((key) => {
            if (typeof data[key] === 'function') {
                this.props[key] = data[key];
            }
        });
    }
    assignUVs(geometry) {
        geometry.computeBoundingBox();
        const max = geometry.boundingBox.max;
        const min = geometry.boundingBox.min;
        const offset = new THREE.Vector2(0 - min.x, 0 - min.y);
        const range = new THREE.Vector2(max.x - min.x, max.y - min.y);
        const pointArray = geometry.attributes.position.array;
        const _uvs = [];
        const len = pointArray.length / 3;
        for (let i = 0; i < len; i++) {
            const v = i * 3;
            _uvs.push((pointArray[v] + offset.x) / range.x, (pointArray[v + 1] + offset.y) / range.y);
        }
        const uvs = new Float32Array(_uvs);
        geometry.attributes.uv = new THREE.BufferAttribute(uvs, 2);
        geometry.attributes.uv.needsUpdate = true;
        // const faces = geometry.faces;
        // console.log('faces', faces, geometry.vertices, geometry.faceVertexUvs);
        // geometry.faceVertexUvs[0] = [];
        // for (let i = 0; i < faces.length; i++) {
        //   const v1 = geometry.vertices[faces[i].a];
        //   const v2 = geometry.vertices[faces[i].b];
        //   const v3 = geometry.vertices[faces[i].c];
        //   geometry.faceVertexUvs[0].push([
        //     new THREE.Vector2((v1.x + offset.x) / range.x, (v1.y + offset.y) / range.y),
        //     new THREE.Vector2((v2.x + offset.x) / range.x, (v2.y + offset.y) / range.y),
        //     new THREE.Vector2((v3.x + offset.x) / range.x, (v3.y + offset.y) / range.y),
        //   ]);
        // }
        // geometry.uvsNeedUpdate = true;
    }
    _draw(width, height, polygon, border, pointsCanvas, roiText, text) {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Draw polygon background color
            if (polygon?.color) {
                ctx.fillStyle = `${polygon.color}`;
            }
            if (border.dash) {
                ctx.setLineDash(border.dash);
            }
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            // Draw border
            ctx.strokeStyle = border.color;
            ctx.lineWidth = border.width * 2; // 有一半绘制在 canvas 外面，看不见，因此需要乘以 2
            ctx.beginPath();
            ctx.moveTo(pointsCanvas[0][0], pointsCanvas[0][1]);
            // ctx.lineTo(171.00428, 618.4897699999999);
            // ctx.lineTo(908, 422);
            for (let i = 1; i < pointsCanvas.length; i++) {
                ctx.lineTo(pointsCanvas[i][0], pointsCanvas[i][1]);
            }
            ctx.closePath();
            ctx.stroke();
            if (this.boldLineNum > -1) {
                // 加粗边
                const num = this.boldLineNum;
                ctx.lineWidth = border.width * 8;
                ctx.strokeStyle = 'white';
                ctx.beginPath();
                ctx.moveTo(pointsCanvas[num][0], pointsCanvas[num][1]);
                ctx.lineTo(pointsCanvas[num + 1][0], pointsCanvas[num + 1][1]);
                ctx.closePath();
                ctx.stroke();
            }
            if (text) {
                // draw text
                ctx.fillStyle = roiText.color;
                ctx.font = this.getFont(roiText.size);
                ctx.textBaseline = 'middle';
                const { width: textWidth } = this.getTextSize(roiText.size, text);
                ctx.fillText(text, (width - textWidth) / 2, height / 2);
            }
            // Generate canvastexture
            const canvasTexture = new THREE.CanvasTexture(canvas);
            canvasTexture.needsUpdate = true;
            return canvasTexture;
        }
        return null;
    }
    getFont(size) {
        return `normal ${size}px Arial`;
    }
    getTextSize(size, text) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.font = this.getFont(size);
        const width = ctx.measureText(text).width;
        const height = ctx.measureText('M').width;
        return { width, height };
    }
    pointsToVertices(points) {
        // 提取数据，封闭图形
        const vertices = [];
        points.forEach((e) => {
            const [x, y, z] = e;
            vertices.push(new THREE.Vector3(x, y, z));
        });
        const [x, y, z] = points[0];
        vertices.push(new THREE.Vector3(x, y, z));
        return vertices;
    }
    // 点的转换：世界坐标系转为工件坐标系
    pointsWorldToLocal(pointsWorld) {
        let minX = Number.MAX_SAFE_INTEGER;
        let minY = Number.MAX_SAFE_INTEGER;
        pointsWorld.forEach((point) => {
            minX = Math.min(minX, point[0]);
            minY = Math.min(minY, point[1]);
        });
        const pointsLocal = JSON.parse(JSON.stringify(pointsWorld));
        pointsLocal.forEach((point) => {
            point[0] -= minX;
            point[1] -= minY;
        });
        return pointsLocal;
    }
    // 点的转换：three 坐标系转为 canvas 坐标系; 两个坐标系成镜像关系
    pointsThreeToCanvas(pointsThree) {
        let minY = Number.MAX_SAFE_INTEGER;
        let maxY = -Number.MAX_SAFE_INTEGER;
        pointsThree.forEach((point) => {
            minY = Math.min(minY, point[1]);
            maxY = Math.max(maxY, point[1]);
        });
        const height = maxY - minY;
        const pointsCanvas = JSON.parse(JSON.stringify(pointsThree));
        pointsCanvas.forEach((point) => {
            point[1] = height - point[1];
        });
        return pointsCanvas;
    }
    getSize(points) {
        let minX = Number.MAX_SAFE_INTEGER;
        let maxX = -Number.MAX_SAFE_INTEGER;
        let minY = Number.MAX_SAFE_INTEGER;
        let maxY = -Number.MAX_SAFE_INTEGER;
        points.forEach((point) => {
            minX = Math.min(minX, point[0]);
            maxX = Math.max(maxX, point[0]);
            minY = Math.min(minY, point[1]);
            maxY = Math.max(maxY, point[1]);
        });
        const width = maxX - minX;
        const height = maxY - minY;
        return { width, height };
    }
    handlePoints(points) {
        const vertices = this.pointsToVertices(points);
        const pointsLocal = this.pointsWorldToLocal(points);
        const pointsCanvas = this.pointsThreeToCanvas(pointsLocal);
        const { width, height } = this.getSize(points);
        return { width, height, vertices, pointsLocal, pointsCanvas };
    }
    getBoldLineNum(points, boldPoints = []) {
        if (!boldPoints?.length)
            return -1;
        const begin = boldPoints[0];
        for (const i in points) {
            const curPoint = points[i];
            if (curPoint.toString() === begin.toString())
                return Number(i);
        }
        return -1;
    }
    onClick() {
        super.onClick();
    }
    dispose() {
        document.body.style.cursor = '';
        this.traverse(function (obj) {
            if (obj instanceof Mesh) {
                obj.geometry?.dispose();
                obj.material?.dispose();
            }
            else {
                if (obj.parent) {
                    obj?.parent.remove(obj);
                }
                obj.clear();
            }
        });
        this.parent?.remove(this);
        this.clear();
    }
    onMouseEnter() {
        const Props = cloneDeep(this.props);
        if (!Props.renderProps.active)
            return;
        document.body.style.cursor = 'pointer';
        Props.renderProps.normal = Props.renderProps.active;
        Props.opacity = 1;
        this.update(Props, false);
        super.onMouseEnter();
        this.isMouseEnter = true;
    }
    onMouseLeave() {
        this.isMouseEnter = false;
        document.body.style.cursor = '';
        this.update(this.props);
        super.onMouseLeave();
    }
}
export default HotArea;
